// src/pages/relatorios/RelatorioMontadores.jsx
import React, { useState, useEffect } from 'react';
import { 
  FaFilter, FaFileExcel, FaSearch, FaCalendarAlt,
  FaIdCard, FaUsers, FaBuilding, FaTools,
  FaSpinner, FaInfoCircle, FaHardHat
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import supabase from '../../supabaseClient';
import * as XLSX from 'xlsx';

const RelatorioMontadores = () => {
  const { hasRole, user, userProfile } = useAuth();
  
  // Estados
  const [credenciais, setCredenciais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expositorLogado, setExpositorLogado] = useState(null);
  const [expositores, setExpositores] = useState([]);
  const [empresasMontadoras, setEmpresasMontadoras] = useState([]);
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    dataInicio: '',
    dataFim: '',
    expositor: '',
    nomeExpositor: '',
    empresaMontadora: '',
    nomeMontador: '',
    cpfMontador: '',
    nomeResponsavel: ''
  });
  
  // Estados para estatísticas
  const [stats, setStats] = useState({
    totalCredenciais: 0,
    totalExpositores: 0,
    totalEmpresasMontadoras: 0,
    totalMontadores: 0
  });

  // Buscar expositor logado baseado no email
  const fetchExpositorLogado = async () => {
    if (!user?.email || hasRole('admin')) {
      return null; // Admin vê todas as credenciais
    }

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('*')
        .eq('email', user.email)
        .eq('ativo', true)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar expositor logado:', error);
      return null;
    }
  };

  // Carregar expositores para o filtro (apenas para admin)
  const fetchExpositores = async () => {
    if (!hasRole('admin')) return;

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa, cnpj, email')
        .eq('ativo', true)
        .order('nome_empresa');
      
      if (error) throw error;
      setExpositores(data || []);
    } catch (error) {
      console.error('Erro ao buscar expositores:', error);
    }
  };

  // Carregar empresas montadoras para o filtro
  const fetchEmpresasMontadoras = async () => {
    try {
      // Buscando empresas montadoras únicas
      const { data, error } = await supabase
        .from('caex_empresas_montadoras')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');
      
      if (error) throw error;
      setEmpresasMontadoras(data || []);
    } catch (error) {
      console.error('Erro ao buscar empresas montadoras:', error);
    }
  };

  // Carregar dados das credenciais de montadores com filtros aplicados
  const fetchCredenciais = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Buscando credenciais de montadores com filtros:', filters);
      
      // Primeiro buscar expositor se for expositor logado
      const expositor = await fetchExpositorLogado();
      setExpositorLogado(expositor);
      
      // Query base para buscar credenciais de montadores
      let query = supabase
        .from('caex_montadores')
        .select(`
          id,
          created_at,
          nome,
          cpf,
          cargo,
          status,
          empresa_montadora_id,
          evento_id,
          data_inicio,
          data_fim,
          caex_empresas_montadoras (
            id,
            nome,
            cnpj,
            responsavel,
            cpf_responsavel,
            telefone,
            expositor_id,
            caex_expositores (
              id,
              nome_empresa,
              cnpj,
              email
            )
          ),
          caex_eventos (
            id,
            nome,
            data_inicio,
            data_fim
          )
        `)
        .eq('status', 'ativo')
        .order('created_at', { ascending: false });

      // Aplicar filtro por expositor se não for admin
      if (!hasRole('admin') && expositor) {
        // Buscar empresas montadoras do expositor logado
        const { data: empresasExpositor } = await supabase
          .from('caex_empresas_montadoras')
          .select('id')
          .eq('expositor_id', expositor.id)
          .eq('ativo', true);
        
        if (empresasExpositor && empresasExpositor.length > 0) {
          const empresasIds = empresasExpositor.map(emp => emp.id);
          query = query.in('empresa_montadora_id', empresasIds);
        } else {
          // Se não tem empresas montadoras, não retorna nada
          setCredenciais([]);
          setStats({
            totalCredenciais: 0,
            totalExpositores: 0,
            totalEmpresasMontadoras: 0,
            totalMontadores: 0
          });
          setLoading(false);
          return;
        }
      }

      // Aplicar filtros de data
      if (filters.dataInicio) {
        query = query.gte('created_at', `${filters.dataInicio}T00:00:00`);
      }

      if (filters.dataFim) {
        query = query.lte('created_at', `${filters.dataFim}T23:59:59`);
      }

      // Filtro por empresa montadora
      if (filters.empresaMontadora) {
        query = query.eq('empresa_montadora_id', filters.empresaMontadora);
      }

      // Filtro por CPF do montador
      if (filters.cpfMontador) {
        query = query.ilike('cpf', `%${filters.cpfMontador}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('Erro ao buscar credenciais:', fetchError);
        throw fetchError;
      }

      console.log('Credenciais carregadas:', data);

      // Verificar se o expositor foi encontrado (apenas para não-admin)
      if (!hasRole('admin') && !expositor) {
        setError('Expositor não encontrado para este usuário. Entre em contato com o administrador.');
        setLoading(false);
        return;
      }

      // Processar dados para o relatório
      let credenciaisProcessadas = [];
      const expositoresUnicos = new Set();
      const empresasUnicas = new Set();

      data.forEach((credencial, index) => {
        const numeroSequencial = index + 1;
        const dataCredencial = new Date(credencial.created_at).toLocaleDateString('pt-BR');
        const nomeExpositor = credencial.caex_empresas_montadoras?.caex_expositores?.nome_empresa || 'Expositor não encontrado';
        const nomeMontadora = credencial.caex_empresas_montadoras?.nome || 'Empresa não encontrada';
        const nomeMontador = credencial.nome;
        const cpfMontador = credencial.cpf;
        const nomeResponsavel = credencial.caex_empresas_montadoras?.responsavel || 'Não informado';
        const cpfResponsavel = credencial.caex_empresas_montadoras?.cpf_responsavel || 'Não informado';
        const telefoneResponsavel = credencial.caex_empresas_montadoras?.telefone || 'Não informado';
        const evento = credencial.caex_eventos?.nome || 'Evento não informado';
        
        credenciaisProcessadas.push({
          numeroSequencial,
          credencialId: credencial.id,
          dataCredencial,
          nomeExpositor,
          nomeMontadora,
          nomeMontador,
          cpfMontador,
          nomeResponsavel,
          cpfResponsavel,
          telefoneResponsavel,
          evento,
          status: credencial.status,
          empresaMontadoraId: credencial.empresa_montadora_id,
          expositorId: credencial.caex_empresas_montadoras?.expositor_id
        });

        // Para estatísticas
        if (credencial.caex_empresas_montadoras?.expositor_id) {
          expositoresUnicos.add(credencial.caex_empresas_montadoras.expositor_id);
        }
        if (credencial.empresa_montadora_id) empresasUnicas.add(credencial.empresa_montadora_id);
      });

      // Aplicar filtros locais que não podem ser aplicados na query SQL
      if (filters.nomeExpositor) {
        credenciaisProcessadas = credenciaisProcessadas.filter(credencial =>
          credencial.nomeExpositor.toLowerCase().includes(filters.nomeExpositor.toLowerCase())
        );
      }

      if (filters.nomeMontador) {
        credenciaisProcessadas = credenciaisProcessadas.filter(credencial =>
          credencial.nomeMontador.toLowerCase().includes(filters.nomeMontador.toLowerCase())
        );
      }

      if (filters.nomeResponsavel) {
        credenciaisProcessadas = credenciaisProcessadas.filter(credencial =>
          credencial.nomeResponsavel.toLowerCase().includes(filters.nomeResponsavel.toLowerCase())
        );
      }

      setCredenciais(credenciaisProcessadas);
      setStats({
        totalCredenciais: credenciaisProcessadas.length,
        totalExpositores: expositoresUnicos.size,
        totalEmpresasMontadoras: empresasUnicas.size,
        totalMontadores: credenciaisProcessadas.length
      });

    } catch (err) {
      console.error('Erro ao carregar credenciais:', err);
      setError(err.message || 'Erro ao carregar dados das credenciais de montadores');
    } finally {
      setLoading(false);
    }
  };

  // Função para exportar para Excel
  const exportToExcel = () => {
    try {
      if (credenciais.length === 0) {
        alert('Não há dados para exportar.');
        return;
      }

      // Preparar dados para o Excel baseado no perfil do usuário
      const dadosExcel = credenciais.map(credencial => {
        if (hasRole('admin')) {
          // Para administradores: todos os campos
          return {
            'Nº': credencial.numeroSequencial,
            'Data': credencial.dataCredencial,
            'Nome do Expositor': credencial.nomeExpositor,
            'Nome da Montadora': credencial.nomeMontadora,
            'Nome do Montador': credencial.nomeMontador,
            'CPF do Montador': credencial.cpfMontador,
            'Nome do Responsável': credencial.nomeResponsavel,
            'CPF do Responsável': credencial.cpfResponsavel,
            'Telefone do Responsável': credencial.telefoneResponsavel
          };
        } else {
          // Para expositores: sem nome do expositor
          return {
            'Nº': credencial.numeroSequencial,
            'Data': credencial.dataCredencial,
            'Nome da Montadora': credencial.nomeMontadora,
            'Nome do Montador': credencial.nomeMontador,
            'CPF do Montador': credencial.cpfMontador,
            'Nome do Responsável': credencial.nomeResponsavel,
            'CPF do Responsável': credencial.cpfResponsavel,
            'Telefone do Responsável': credencial.telefoneResponsavel
          };
        }
      });

      // Criar workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dadosExcel);

      // Configurar larguras das colunas
      const colWidths = hasRole('admin') ? [
        { wch: 8 },  // Nº
        { wch: 15 }, // Data
        { wch: 30 }, // Nome do Expositor
        { wch: 25 }, // Nome da Montadora
        { wch: 25 }, // Nome do Montador
        { wch: 15 }, // CPF do Montador
        { wch: 25 }, // Nome do Responsável
        { wch: 15 }, // CPF do Responsável
        { wch: 20 }  // Telefone do Responsável
      ] : [
        { wch: 8 },  // Nº
        { wch: 15 }, // Data
        { wch: 25 }, // Nome da Montadora
        { wch: 25 }, // Nome do Montador
        { wch: 15 }, // CPF do Montador
        { wch: 25 }, // Nome do Responsável
        { wch: 15 }, // CPF do Responsável
        { wch: 20 }  // Telefone do Responsável
      ];
      
      worksheet['!cols'] = colWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Credenciais Montadores');

      // Gerar nome do arquivo com data/hora atual
      const now = new Date();
      const dataHora = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const nomeArquivo = `credenciais_montadores_${dataHora}.xlsx`;

      // Salvar arquivo
      XLSX.writeFile(workbook, nomeArquivo);

      console.log('Excel exportado com sucesso:', nomeArquivo);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert('Erro ao exportar para Excel. Tente novamente.');
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    if (user?.email) {
      fetchExpositores();
      fetchEmpresasMontadoras();
      fetchCredenciais();
    }
  }, [user]);

  // Aplicar filtros
  const handleApplyFilters = () => {
    fetchCredenciais();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      dataInicio: '',
      dataFim: '',
      expositor: '',
      nomeExpositor: '',
      empresaMontadora: '',
      nomeMontador: '',
      cpfMontador: '',
      nomeResponsavel: ''
    });
    setTimeout(() => {
      fetchCredenciais();
    }, 100);
  };

  // Handler para mudanças nos filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Renderizar estatísticas
  const renderStats = () => (
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-icon">
          <FaIdCard />
        </div>
        <div className="stat-content">
          <h3>{stats.totalCredenciais}</h3>
          <p>Total de Credenciais</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaHardHat />
        </div>
        <div className="stat-content">
          <h3>{stats.totalMontadores}</h3>
          <p>Montadores Cadastrados</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaBuilding />
        </div>
        <div className="stat-content">
          <h3>{stats.totalEmpresasMontadoras}</h3>
          <p>Empresas Montadoras</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaUsers />
        </div>
        <div className="stat-content">
          <h3>{stats.totalExpositores}</h3>
          <p>Expositores Atendidos</p>
        </div>
      </div>
    </div>
  );

  // Renderizar filtros
  const renderFilters = () => (
    <div className="filters-section">
      <div className="filters-grid">
        {/* Primeira linha - Datas */}
        <div className="filter-group">
          <label>Data Início</label>
          <input
            type="date"
            value={filters.dataInicio}
            onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Data Fim</label>
          <input
            type="date"
            value={filters.dataFim}
            onChange={(e) => handleFilterChange('dataFim', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Nome do Expositor</label>
          <input
            type="text"
            placeholder="Digite o nome do expositor..."
            value={filters.nomeExpositor}
            onChange={(e) => handleFilterChange('nomeExpositor', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Empresa Montadora</label>
          <select
            value={filters.empresaMontadora}
            onChange={(e) => handleFilterChange('empresaMontadora', e.target.value)}
            className="filter-input"
          >
            <option value="">Todas as Empresas</option>
            {empresasMontadoras.map(empresa => (
              <option key={empresa.id} value={empresa.id}>
                {empresa.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Segunda linha */}
        <div className="filter-group">
          <label>Nome do Montador</label>
          <input
            type="text"
            placeholder="Digite o nome do montador..."
            value={filters.nomeMontador}
            onChange={(e) => handleFilterChange('nomeMontador', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>CPF do Montador</label>
          <input
            type="text"
            placeholder="Digite o CPF do montador..."
            value={filters.cpfMontador}
            onChange={(e) => handleFilterChange('cpfMontador', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Nome do Responsável</label>
          <input
            type="text"
            placeholder="Digite o nome do responsável..."
            value={filters.nomeResponsavel}
            onChange={(e) => handleFilterChange('nomeResponsavel', e.target.value)}
            className="filter-input"
          />
        </div>
      </div>
      
      <div className="filters-actions">
        <button 
          className="btn-filter apply"
          onClick={handleApplyFilters}
          disabled={loading}
        >
          <FaSearch />
          Aplicar Filtros
        </button>
        
        <button 
          className="btn-filter clear"
          onClick={handleClearFilters}
          disabled={loading}
        >
          Limpar Filtros
        </button>
      </div>
    </div>
  );

  // Renderizar tabela
  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <FaSpinner className="loading-spinner" />
          <p>Carregando dados das credenciais...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <FaInfoCircle className="error-icon" />
          <h3>Erro ao carregar dados</h3>
          <p>{error}</p>
          {error.includes('Expositor não encontrado') ? (
            <div className="error-details">
              <p><strong>Email do usuário:</strong> {user?.email}</p>
              <p>Verifique se existe um expositor cadastrado com este email e se está ativo no sistema.</p>
            </div>
          ) : (
            <button className="btn-retry" onClick={fetchCredenciais}>
              Tentar Novamente
            </button>
          )}
        </div>
      );
    }

    if (credenciais.length === 0) {
      return (
        <div className="empty-container">
          <FaHardHat className="empty-icon" />
          <h3>Nenhuma credencial encontrada</h3>
          <p>Não há credenciais de montadores que correspondam aos filtros aplicados.</p>
        </div>
      );
    }

    // Colunas condicionais baseadas no perfil do usuário
    const renderTableHeaders = () => {
      if (hasRole('admin')) {
        return (
          <tr>
            <th>Nº</th>
            <th>Data</th>
            <th>Nome do Expositor</th>
            <th>Nome da Montadora</th>
            <th>Nome do Montador</th>
            <th>CPF do Montador</th>
            <th>Nome do Responsável</th>
            <th>CPF do Responsável</th>
            <th>Telefone do Responsável</th>
          </tr>
        );
      } else {
        return (
          <tr>
            <th>Nº</th>
            <th>Data</th>
            <th>Nome da Montadora</th>
            <th>Nome do Montador</th>
            <th>CPF do Montador</th>
            <th>Nome do Responsável</th>
            <th>CPF do Responsável</th>
            <th>Telefone do Responsável</th>
          </tr>
        );
      }
    };

    const renderTableRows = () => {
      return credenciais.map((credencial) => {
        if (hasRole('admin')) {
          return (
            <tr key={credencial.credencialId}>
              <td>{credencial.numeroSequencial}</td>
              <td>{credencial.dataCredencial}</td>
              <td>{credencial.nomeExpositor}</td>
              <td>{credencial.nomeMontadora}</td>
              <td>{credencial.nomeMontador}</td>
              <td>{credencial.cpfMontador}</td>
              <td>{credencial.nomeResponsavel}</td>
              <td>{credencial.cpfResponsavel}</td>
              <td>{credencial.telefoneResponsavel}</td>
            </tr>
          );
        } else {
          return (
            <tr key={credencial.credencialId}>
              <td>{credencial.numeroSequencial}</td>
              <td>{credencial.dataCredencial}</td>
              <td>{credencial.nomeMontadora}</td>
              <td>{credencial.nomeMontador}</td>
              <td>{credencial.cpfMontador}</td>
              <td>{credencial.nomeResponsavel}</td>
              <td>{credencial.cpfResponsavel}</td>
              <td>{credencial.telefoneResponsavel}</td>
            </tr>
          );
        }
      });
    };

    return (
      <div className="table-container">
        <table className="report-table">
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="relatorio-container">
      {/* Header com estatísticas */}
      <div className="relatorio-header">
        {/* Informação do expositor se não for admin */}
        {!hasRole('admin') && expositorLogado && (
          <div className="expositor-info">
            <h4>Relatório de: {expositorLogado.nome_empresa}</h4>
            <p>CNPJ: {expositorLogado.cnpj}</p>
          </div>
        )}
        
        <div className="header-controls">
          <button 
            className={`btn-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
          
          <div className="export-buttons">
            <button 
              className="btn-export excel" 
              onClick={exportToExcel}
              disabled={loading || credenciais.length === 0}
            >
              <FaFileExcel />
              Excel
            </button>
          </div>
        </div>

        {renderStats()}
      </div>

      {/* Filtros */}
      {showFilters && renderFilters()}

      {/* Tabela de dados */}
      <div className="relatorio-content">
        {renderTable()}
      </div>
    </div>
  );
};

export default RelatorioMontadores;